import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import RentalPackagesPanel from '../../components/rental/RentalPackagesPanel';
import RentalZoneHierarchyTable from '../../components/rental/RentalZoneHierarchyTable';
import { listRentalZones } from '../../services/rentalManagementService';
import { listZones } from '../../services/zoneService';

export default function RentalManagementPage() {
  const navigate = useNavigate();
  const { zoneCode } = useParams<{ zoneCode?: string }>();

  const { data: rentalZones = [], isLoading: rentalLoading } = useQuery({
    queryKey: ['rental-zones-hierarchy'],
    queryFn: async () => {
      const res = await listRentalZones({ limit: 500 });
      return res.data;
    },
    enabled: !zoneCode,
  });

  const { data: zoneMeta = [], isLoading: zonesLoading } = useQuery({
    queryKey: ['zones-meta-rental-hierarchy'],
    queryFn: async () => {
      const res = await listZones({ limit: 500 });
      return (res.rows || []).map((z) => ({
        _id: z._id,
        primaryZoneId: z.primaryZoneId,
        zoneLevel: z.zoneLevel,
      }));
    },
    enabled: !zoneCode,
  });

  const { data: zoneInfo } = useQuery({
    queryKey: ['zone-by-code', zoneCode],
    queryFn: async () => {
      const res = await listZones({ limit: 500 });
      return res.rows?.find((z) => z.code === zoneCode);
    },
    enabled: !!zoneCode,
  });

  if (zoneCode) {
    const zoneTitle = zoneInfo?.name || zoneCode;
    return (
      <PageShell>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/transport/rental-management')}>
            All Zones
          </Button>
        </Stack>
        <PageHeader
          title={`Rental Packages — ${zoneTitle || zoneCode}`}
          subtitle="Manage hour/km rental packages for this zone. One screen for all transport rental pricing and time details."
        />
        <RentalPackagesPanel
          lockedZoneCode={zoneCode}
          lockedZoneName={zoneInfo?.name}
          unifiedTransportRental
          subtitle="Add hour/km packages with per-vehicle pricing for all transport rental services from one screen."
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Rental Management"
        subtitle="Primary zones are listed below. Use the eye icon to reveal secondary zones, and the menu to edit rental fare."
        gradient
      />

      <RentalZoneHierarchyTable
        rentalZones={rentalZones}
        zoneMeta={zoneMeta}
        isLoading={rentalLoading || zonesLoading}
        onEditRentalFare={(code) => navigate(`/transport/rental-management/${encodeURIComponent(code)}`)}
        onViewPackages={(code) => navigate(`/transport/rental-management/${encodeURIComponent(code)}`)}
      />
    </PageShell>
  );
}
